"use client"

import * as Sentry from '@sentry/react'
import { useState, useEffect } from "react"
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom"
import { HelmetProvider } from 'react-helmet-async'
import Home from "./components/LandingPage/Home"
import Features from "./components/LandingPage/Features"
import Benefits from "./components/LandingPage/Benefits"
import MobileApp from "./components/LandingPage/MobileApp"
import FAQ from "./components/LandingPage/FAQ"
import Contact from "./components/LandingPage/Contact"
import Blog from "./components/LandingPage/Blog"
import BlogPost from "./components/LandingPage/BlogPost"
import Login from "./components/Login/Login"
import DashboardRouter from "./components/Routing/DashboardRouter"
import PrivateRoute from "./components/Routing/PrivateRoute"
import { AdvancedNavbarExample } from "./components/demo/AdvancedNavbarExample"
import ForgotPassword from "./components/Login/forgot-password"
import { logEvent } from "firebase/analytics"
import { analytics } from "./lib/firebase"
import { Toaster } from "react-hot-toast"
import ScheduleDemo from "./components/LandingPage/ScheduleDemo"
import PrivacyPolicy from "./components/LandingPage/PrivacyPolicy"
import TermsConditions from "./components/LandingPage/TermsConditions"
import RefundPolicy from "./components/LandingPage/RefundPolicy"
import SchoolManagement from "./components/LandingPage/SchoolManagement"
import BusManagement from "./components/LandingPage/BusManagement"
import InventoryManagement from "./components/LandingPage/InventoryManagement"
import SEOHead from "./components/SEO/SEOHead"
import GoogleAnalytics from "./components/SEO/GoogleAnalytics"
import MobileTrackingApp from "./components/Common/Mobiletrackingapp"
import MobileTrackingApp2 from "./components/Common/Mobiletrackingapp2"
import MobileBillingApp from "./components/Common/MobileBillingApp"
import VehicleStopsPreview from "./components/vehicle/VehicleStopsPreview"
import NotFound from "./components/Common/NotFound"
import AccessDenied from "./pages/AccessDenied"
import { getStoredAuth, clearStoredAuth } from "./utils/authStorage"
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext"

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [pathname])

  return null
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)

  useEffect(() => {
    // Log initial page view
    logEvent(analytics, "page_view", {
      page_title: "App Initialization",
    })

    const messageListener = window.addEventListener("message", (nativeEvent) => {})
    return messageListener
  }, [])

  useEffect(() => {
    const { token, user: storedUser, vehicle } = getStoredAuth()
    if (token && (storedUser || vehicle)) {
      setIsAuthenticated(true)
      setUser(storedUser || vehicle)
    }
  }, [])

  const updateAuthState = (token, userData) => {
    if (token && userData) {
      setIsAuthenticated(true)
      setUser(userData)
    } else {
      clearStoredAuth()
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  return (
    <HelmetProvider>
      <div className="App">
       
        <SEOHead />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: "#333",
              color: "#fff",
            },
          }}
        />
        <BrowserRouter>
          <ScrollToTopOnRouteChange />
          <FeatureFlagProvider>
            <GoogleAnalytics />
          <Routes>
          {/* Mobile Tracking App - Standalone route without Navbar/Sidebar */}
          <Route path="/mobile-tracking" element={<MobileTrackingApp />} />
          <Route path="/mobile-tracking-2" element={<MobileTrackingApp2 />} />
          <Route path="/mobile-billing" element={<MobileBillingApp />} />
          <Route path="/mobile-stops-preview" element={<VehicleStopsPreview />} />
          <Route path="/dashboard/stopspreview" element={<VehicleStopsPreview />} />
          
          <Route
            path="*"
            element={
              <>
                <Routes>
                  <Route
                    path="/"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                      ) : (
                        <>
                          <AdvancedNavbarExample
                            isAuthenticated={isAuthenticated}
                            user={user}
                            onLogout={() => updateAuthState(null, null)}
                          />
                          <div className="min-h-screen bg-gray-100">
                            <Home isAuthenticated={isAuthenticated} />
                          </div>
                        </>
                      )
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                      ) : (
                        <>
                          <AdvancedNavbarExample
                            isAuthenticated={isAuthenticated}
                            user={user}
                            onLogout={() => updateAuthState(null, null)}
                          />
                          <div className="min-h-screen bg-gray-100">
                            <Login updateAuthState={updateAuthState} />
                          </div>
                        </>
                      )
                    }
                  />
                  <Route path="/forgot-password" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><ForgotPassword /></div></>} />
                  <Route path="/access-denied" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><AccessDenied /></div></>} />
                  <Route
                    path="/dashboard/*"
                    element={
                      <PrivateRoute isAuthenticated={isAuthenticated}>
                        <div className="flex flex-col h-screen overflow-hidden">
                          <div className="flex-shrink-0">
                            <AdvancedNavbarExample
                              isAuthenticated={isAuthenticated}
                              user={user}
                              onLogout={() => updateAuthState(null, null)}
                            />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <DashboardRouter onLogout={() => updateAuthState(null, null)} />
                          </div>
                        </div>
                      </PrivateRoute>
                    }
                  />
                  {/* Schedule Demo - public page */}
                  <Route path="/schedule-demo" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><ScheduleDemo /></div></>} />

                  {/* Policy Pages */}
                  <Route path="/privacy-policy" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><PrivacyPolicy /></div></>} />
                  <Route path="/terms-conditions" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><TermsConditions /></div></>} />
                  <Route path="/refund-policy" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><RefundPolicy /></div></>} />

                  {/* Product Pages */}
                  <Route path="/products/school-management" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><SchoolManagement /></div></>} />
                  <Route path="/products/bus-management" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><BusManagement /></div></>} />
                  <Route path="/products/inventory-management" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><InventoryManagement /></div></>} />

                  <Route path="/features" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><Features /></div></>} />
                  <Route path="/benefits" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><Benefits /></div></>} />
                  <Route path="/mobile-app" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><MobileApp /></div></>} />
                  <Route path="/blog" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><Blog /></div></>} />
                  <Route path="/blog/:slug" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><BlogPost /></div></>} />
                  <Route path="/faq" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><FAQ /></div></>} />
                  <Route path="/contact" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><Contact /></div></>} />

                  {/* Backward-compatible aliases */}
                  <Route path="/mobile" element={<Navigate to="/mobile-app" replace />} />
                  <Route path="/pricing" element={<Navigate to="/benefits" replace />} />

                  <Route path="*" element={<><AdvancedNavbarExample isAuthenticated={isAuthenticated} user={user} onLogout={() => updateAuthState(null, null)} /><div className="min-h-screen bg-gray-100"><NotFound /></div></>} />
                </Routes>
              </>
            }
          />
        </Routes>
          </FeatureFlagProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  )
}

export default App
